import type { CountryCode } from 'libphonenumber-js'

export const API_URL = {
  logError: '/frontend-log',
  login: '/user/login',
  register: '/user/register',
  forgetPass: '/user/forget-pass',
  resetPass: '/user/reset-valid',
  changePass: '/api/user/reset-pass',
  verifyEmail: '/user/verify-email', // after user clicks the link in email
  sendEmailVerify: '/api/user/email-verify', // sent email with link to user
  logout: '/user/logout',
  getUserInfo: '/api/user/info',
  postUserInfo: '/api/user/info',
  getUserAddresses: '/api/user/addresses',
  postUserAddress: '/api/user/address',
  removeUserAddress: '/api/user/address-remove',
  getUserOrder: '/api/user/order-details',
  getUserOrders: '/api/user/orders',
  userCreditBalance: '/api/user/balance',
  userCreditTrans: '/api/user/balance-transactions',
  userCashBalance: '/api/user/cash/get',
  userCashTrans: '/api/user/cash/transactions', // +action
  cashWithdraw: '/api/user/cash/withdraw',
  cashWithdrawCancel: '/api/user/cash/withdraw-cancel',
  socialLogin: '/services/login/socialite/',
  category: '/home/category-competitions',
  competitionDetail: '/competitions/competition-details',
  instantWinList: '/competitions/competition-instant-wins',
  categoryList: '/competitions/catalogs',
  competitionList: '/competitions/list',
  gaLandingPage: '/ga-landing-page/', // 付费搜索落地页：这个赛事要关掉哪几块
  recommend: '/competitions/recommend',
  banners: '/home/banners',
  subscribe: '/home/subscribe',
  receiveNotif: '/home/receive-notifications',
  stats: '/home/stats',
  winners: '/winners/list',
  draws: '/draw-list',
  entries: '/entry-list',
  entryInfo: '/entry-info',
  entryDetail: '/entry-details',
  cartCount: '/cart/count',
  addToCart: '/cart/add-to-cart',
  cartDetail: '/cart/details',
  placeOrder: '/api/cart/place-order',
  cartValidate: '/api/cart/validate',
  checkout: '/api/cart/checkout',
  checkoutConfirm: '/api/cart/checkout-confirm',
  checkoutSubmit: '/api/cart/checkout-submit',
  // 购物车卡表单默认展开:打开购物车就要一个 payment session(不下单)
  cartCardSession: '/api/cart/card-session',
  applePayCbBarclays: '/api/service/apple-pay/callback',
  applePayCbCheckout: '/api/service/checkout/callbackApplePay',
  paymentLog: '/api/payment-log',
  clearCart: '/cart/clear',
  updateCart: '/cart/update',
  orderDetail: '/api/order/details',
  reorder: '/api/cart/reorder',
  addCoupon: '/cart/add-coupon',
  removeCoupon: '/cart/remove-coupon',
  useCreditBalance: '/api/cart/use-balance', // credit
  useCashBalance: '/api/cart/use-wallet', // cash
  donations: '/donations',
  topUp: '/api/user/top-up/charge',
  topUpOptions: '/api/user/top-up/options',
  topUpHistory: '/api/user/top-up/payments',
  readNotif: '/api/user/read-notifications',
  selectReward: '/api/user/reward-select',
  captureContext: '/api/service/barclaycard/token',
  barclayEnroll: '/api/service/barclaycard/enrollment',
  allUserTix: '/api/user/tickets/get',
  usrAffiliateInfo: '/api/user/affiliate',
  usrAffiliateTrans: '/api/user/affiliate/transactions',
  usrAffCampaigns: '/api/user/affiliate/campaigns',
  usrAffCampCreate: '/api/user/affiliate/campaign-create',
  usrAffReferrals: '/api/user/affiliate/referrals',
  usrAffRefDetails: '/api/user/affiliate/referral-detail',
  usrAffWithdraw: '/api/user/affiliate/withdraw',
  campaignHit: '/campaign-hit',
  adsClick: '/ads-click',
  getKycInfo: '/api/user/kyc-info',
  postKycInfo: '/api/user/kyc-info',
  getProfiles: '/api/user/cash/profiles',
  postProfileUpsert: '/api/user/cash/profileUpsert',
  getS3Configs: '/api/user/s3-configs',
  getCards: '/api/user/cash/cards',
  // 只拿能在购物车付款的卡:提现页注册的卡从没经发卡行验证,钱包来源的卡直接点钱包按钮更快
  getPayableCards: '/api/user/cash/cards?usage=payment',
  lastPaymentMethod: '/api/cart/last-payment-method',
  cardRegister: '/api/user/cash/card-register',
  cardSession: '/api/user/cash/card-session',
  cardDefault: '/api/user/cash/card-default',
  cardRemove: '/api/user/cash/card-remove',
  createWallet: '/api/user/cash/add',
  mergeAccountRequest: '/api/user/merge-account/request',
  redirectResolve: '/redirect/resolve',
  feedback: '/api/feedback',
  feedbackConfig: '/api/feedback/config',
  complaint: '/api/complaint',
  getSaferPlaying: '/api/user/safer-playing',
  getSaferPlayingClosurePreview: '/api/user/safer-playing/closure-preview',
  postSaferPlayingLimit: '/api/user/safer-playing/limit',
  postSaferPlayingSuspend: '/api/user/safer-playing/suspend',
  postSaferPlayingClose: '/api/user/safer-playing/close',
}

export const COUNTRY_LIST: Array<{ name: string; code: CountryCode }> = [
  { name: 'United Kingdom', code: 'GB' },
  { name: 'Republic of Ireland', code: 'IE' },
]

export const IE_COUNTY_LIST = [
  { code: 'CW', name: 'Carlow' },
  { code: 'CN', name: 'Cavan' },
  { code: 'CE', name: 'Clare' },
  { code: 'CO', name: 'Cork' },
  { code: 'DL', name: 'Donegal' },
  { code: 'D', name: 'Dublin' },
  { code: 'G', name: 'Galway' },
  { code: 'KY', name: 'Kerry' },
  { code: 'KE', name: 'Kildare' },
  { code: 'KK', name: 'Kilkenny' },
  { code: 'LS', name: 'Laois' },
  { code: 'LM', name: 'Leitrim' },
  { code: 'LK', name: 'Limerick' },
  { code: 'LD', name: 'Longford' },
  { code: 'LH', name: 'Louth' },
  { code: 'MO', name: 'Mayo' },
  { code: 'MH', name: 'Meath' },
  { code: 'MN', name: 'Monaghan' },
  { code: 'OY', name: 'Offaly' },
  { code: 'RN', name: 'Roscommon' },
  { code: 'SO', name: 'Sligo' },
  { code: 'TA', name: 'Tipperary' },
  { code: 'WD', name: 'Waterford' },
  { code: 'WH', name: 'Westmeath' },
  { code: 'WX', name: 'Wexford' },
  { code: 'WW', name: 'Wicklow' },
]

export const LATEST_WINNERS_PER_PAGE = 14 // must be different from winners page perPage

export const SOCIAL_MEDIA_FACEBOOK = 'https://www.facebook.com/profile.php?id=100077330920045'

/** 订单状态,与后端 App\Models\Orders\Order::STATUS_* 对齐 */
export const ORDER_STATUS = {
  PAID_OVER_DUE: -3,
  REFUNDED: -2,
  CANCELLED: -1,
  CREATED: 0,
  PENDING: 1,
  PAID: 2,
  COMPLETED: 3,
  OVER_PAID: 4,
} as const

/** 能不能「再来一单」——必须和后端 Cart::reorder() 的白名单一致,否则按钮点下去只会拿到 7009 */
export const isReorderable = (status?: number): boolean =>
  status !== undefined &&
  (
    [
      ORDER_STATUS.PAID,
      ORDER_STATUS.COMPLETED,
      ORDER_STATUS.REFUNDED,
      ORDER_STATUS.CANCELLED,
      ORDER_STATUS.PAID_OVER_DUE,
      ORDER_STATUS.OVER_PAID,
    ] as number[]
  ).includes(status)
